import React, { useState, useEffect } from "react";
import { PageTitle } from "../../components";
import {
  Table,
  Space,
  Button,
  Form,
  Input,
  Select,
  Modal,
  Tabs,
  message,
  Skeleton,
  Popconfirm,
  Result,
} from "antd";
import dayjs from "dayjs";
import { Routes, Route, useParams, Link, useNavigate } from "react-router-dom";
import type { ColumnsType } from "antd/lib/table";
import { ArcheryProp } from "../../_helpers/type";
import { CURRENT_CLUB_ID } from "../../_helpers/constants";
import { teamsApi, userClubsApi } from "../../_helpers/apiService";
import { useSelector } from "react-redux";

const { Option } = Select;
const { TabPane } = Tabs;

interface DataType {
  key?: string;
  name?: string;
  club_id?: string;
}

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 14 },
};

function Team() {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [data, setData] = useState<DataType>({});
  const [archers, setArchers] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);

  // Get ranks from Redux store
  const ranks = useSelector((state: any) => state.ranks || {});

  let { teamId } = useParams();

  const columns: ColumnsType<ArcheryProp> = [
    {
      title: "№",
      dataIndex: "id",
      key: "id",
      width: 80,
      render: (text, row, index) => <>{index + 1}</>,
    },
    {
      title: "Овог",
      dataIndex: "last_name",
      key: "last_name",
      width: 120,
    },
    {
      title: "Нэр",
      dataIndex: "first_name",
      key: "first_name",
      width: 120,
    },
    {
      title: "Цол",
      dataIndex: "rank_id",
      key: "rank_id",
      width: 150,
      render: (value, record) => {
        if (!value) return <span>-</span>;
        const currentRank = ranks[value];
        return currentRank ? currentRank.name : <span>-</span>;
      },
    },
    {
      title: "Имэйл хаяг",
      dataIndex: "email",
      key: "email",
      width: 200,
    },
    {
      title: "Үйлдэл",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Space size="middle">
          <Link to={`/archers/${record.user_id}`} key={record.user_id}>
            Засах
          </Link>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (teamId) {
      fetchTeamData();
    }
  }, [teamId]);

  const fetchTeamData = async () => {
    if (!teamId) return;

    setLoading(true);
    try {
      // Fetch team data
      const teamResponse = await teamsApi.getById(parseInt(teamId));
      const teamData = teamResponse.data;

      // Check if the team belongs to the current club
      if (teamData && teamData.club_id === CURRENT_CLUB_ID) {
        setData(teamData);
        form.setFieldsValue(teamData);
        setHasAccess(true);

        // Fetch team members
        const membersResponse = await userClubsApi.getWithDetails(CURRENT_CLUB_ID, {
          team_id: parseInt(teamId),
          limit: 1000
        });

        setArchers(membersResponse.data.items);
      } else {
        setHasAccess(false);
      }
    } catch (error: any) {
      console.error("Error fetching team data:", error);
      message.error("Багийн мэдээлэл татахад алдаа гарлаа");
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  const onFinish = () => {
    form
      .validateFields()
      .then(async (values) => {
        if (teamId) {
          try {
            await teamsApi.update(parseInt(teamId), {
              name: values.name,
              club_id: CURRENT_CLUB_ID,
            });

            message.success("Амжилттай.");
            fetchTeamData();
          } catch (error: any) {
            console.error("Error updating team:", error);
            message.error("Баг засахад алдаа гарлаа");
          }
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  if (loading) {
    return (
      <>
        <PageTitle loading={true} title="Уншиж байна..." right={null} />
        <div className="page-content-wrapper">
          <div className="container-fluid">
            <div className="row">
              <div className="col-lg-12">
                <div className="card">
                  <div className="card-body">
                    <Skeleton loading={true} active />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!hasAccess) {
    return (
      <>
        <PageTitle title="Хандах эрхгүй" right={null} />
        <div className="page-content-wrapper">
          <div className="container-fluid">
            <div className="row">
              <div className="col-lg-12">
                <div className="card">
                  <div className="card-body">
                    <Result
                      status="403"
                      title="Хандах эрхгүй"
                      subTitle="Уучлаарай, багийн мэдээлэл олдсонгүй."
                      extra={
                        <Button
                          type="primary"
                          onClick={() => navigate("/teams")}
                        >
                          Буцах
                        </Button>
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {data ? (
        <PageTitle
          loading={loading}
          title={`${data?.name}`}
          description={"Багийн профайл"}
          right={null}
          breadcrumbItems={[
            { link: "/teams", title: "Багууд" },
            { title: `${data?.name} ` },
          ]}
        />
      ) : (
        ""
      )}
      <div className="page-content-wrapper">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <Skeleton loading={loading} active>
                    <Form {...formItemLayout} form={form} onFinish={onFinish}>
                      <Form.Item
                        label="Нэр"
                        name="name"
                        rules={[{ required: true, message: "Нэр оруулна уу!" }]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item wrapperCol={{ offset: 6, span: 16 }}>
                        <Button type="primary" htmlType="submit">
                          Хадгалах
                        </Button>
                      </Form.Item>
                    </Form>

                    <div className="table-responsive">
                      <h4>Харваачид</h4>
                      <Table
                        columns={columns}
                        dataSource={archers}
                        loading={loading}
                        rowKey={(record) => record.user_id}
                        scroll={{ x: 'max-content' }}
                      />
                    </div>
                  </Skeleton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default Team;
